import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Bell, Moon, Smartphone, ChevronLeft, Zap, Flame, Users, Trophy, Sparkles, Globe, MessageSquare, User as UserIcon, Check, X, Loader } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { User, NotificationSettings, SupportedLanguage } from '../types';
import { FeedbackForm } from './FeedbackForm';
import { checkUsernameAvailability, canChangeUsername, updateProfile } from '../services/profileService';

const DEFAULT_SETTINGS: NotificationSettings = {
  categories: {
    motivational: true,
    social: true,
    challenge: true,
    system: true,
    ai_insight: true
  },
  doNotDisturb: {
    start: '22:00',
    end: '07:00'
  },
  maxPerDay: 5
};

type CategoryKey = keyof NotificationSettings['categories'];

const UsernameChangeSection = ({ user, t }: { user: User, t: (key: string, options?: any) => string }) => {
  const [newUsername, setNewUsername] = useState(user.username || '');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const changeStatus = canChangeUsername(user);

  const handleCheckAvailability = async (username: string) => {
    if (!username.trim()) {
      setIsAvailable(null);
      setError('');
      return;
    }

    const cleanUsername = username.trim().toLowerCase();
    if (cleanUsername.length < 3) {
      setError(t('settings.username_min_length'));
      setIsAvailable(false);
      return;
    }

    if (!/^[a-z0-9_]+$/.test(cleanUsername)) {
      setError(t('settings.username_invalid_chars'));
      setIsAvailable(false);
      return;
    }

    setChecking(true);
    setError('');
    try {
      const available = await checkUsernameAvailability(cleanUsername, user.id);
      setIsAvailable(available);
      if (!available) {
        setError(t('settings.username_taken'));
      }
    } catch (err: any) {
      setError(err.message || t('settings.username_check_error'));
      setIsAvailable(false);
    } finally {
      setChecking(false);
    }
  };

  const handleSave = async () => {
    if (!newUsername.trim()) {
      setError(t('settings.username_empty'));
      return;
    }

    if (!changeStatus.canChange) {
      setError(t('settings.username_change_cooldown', { days: changeStatus.daysRemaining }));
      return;
    }

    if (!isAvailable) {
      setError(t('settings.username_check_first'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await updateProfile(user.id, {
        username: newUsername.trim().toLowerCase(),
        usernameLastChanged: serverTimestamp()
      });
      setIsEditing(false);
      setIsAvailable(null);
    } catch (err: any) {
      setError(err.message || t('settings.username_update_error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing && user.username) {
    return (
      <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-2xl border border-slate-700/50">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-slate-900 rounded-xl text-emerald-500">
            <UserIcon size={20}/>
          </div>
          <div>
            <h4 className="text-xs font-black uppercase text-white tracking-widest">{t('settings.username')}</h4>
            <p className="text-sm text-cyan-400 font-bold">@{user.username}</p>
            {!changeStatus.canChange && (
              <p className="text-[10px] text-slate-500 mt-1">
                {t('settings.username_change_days', { days: changeStatus.daysRemaining })}
              </p>
            )}
          </div>
        </div>
        {changeStatus.canChange && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl text-xs font-black uppercase hover:bg-emerald-500/20 transition-all"
          >
            {t('settings.username_edit')}
          </button>
        )}
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div className="p-4 bg-slate-800/30 rounded-2xl border border-slate-700/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-slate-900 rounded-xl text-emerald-500">
              <UserIcon size={20}/>
            </div>
            <div>
              <h4 className="text-xs font-black uppercase text-white tracking-widest">{t('settings.username')}</h4>
              <p className="text-sm text-slate-500">{t('settings.username_no_set')}</p>
            </div>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all"
          >
            {t('settings.username_set')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-800/30 rounded-2xl border border-slate-700/50 space-y-3">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-slate-900 rounded-xl text-emerald-500">
          <UserIcon size={20}/>
        </div>
        <div className="flex-1">
          <h4 className="text-xs font-black uppercase text-white tracking-widest mb-2">{t('settings.username')}</h4>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newUsername}
              onChange={(e) => {
                const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setNewUsername(val);
                setIsAvailable(null);
                setError('');
              }}
              onBlur={() => handleCheckAvailability(newUsername)}
              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl p-3 text-sm text-white outline-none focus:border-emerald-500"
              placeholder={t('settings.username_placeholder')}
              disabled={saving || checking}
            />
            {checking && <Loader className="animate-spin text-slate-500" size={20} />}
            {isAvailable === true && <Check className="text-emerald-400" size={20} />}
            {isAvailable === false && <X className="text-red-400" size={20} />}
          </div>
          {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
          {isAvailable === true && !error && (
            <p className="text-emerald-400 text-xs mt-1">{t('settings.username_available')}</p>
          )}
          {!changeStatus.canChange && (
            <p className="text-orange-400 text-xs mt-1">
              {t('settings.username_cooldown_warning', { days: changeStatus.daysRemaining })}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !isAvailable || !changeStatus.canChange}
          className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        <button
          onClick={() => {
            setNewUsername(user.username || '');
            setIsEditing(false);
            setError('');
            setIsAvailable(null);
          }}
          disabled={saving}
          className="px-4 py-2 bg-slate-800 text-slate-400 rounded-xl text-xs font-black uppercase hover:bg-slate-700 transition-all"
        >
          {t('settings.cancel')}
        </button>
      </div>
    </div>
  );
};

export const NotificationSettingsView = ({ user, onBack, lang, setLang, currentView }: { user: User, onBack: () => void, lang: SupportedLanguage, setLang: (l: SupportedLanguage) => void, currentView?: string }) => {
  const { t, i18n } = useTranslation();
  const [showFeedback, setShowFeedback] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);

  const updateSettings = async (newSettings: Partial<NotificationSettings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    await updateDoc(doc(db, "users", user.id), {
      notificationSettings: updated
    });
  };

  const toggleCategory = (cat: CategoryKey) => {
    const currentCategories = settings.categories;
    const updatedCats = { ...currentCategories, [cat]: !currentCategories[cat] };
    updateSettings({ categories: updatedCats });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value as SupportedLanguage;
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('vitacore_lang', newLang);
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-2xl mx-auto animate-fadeIn">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={onBack} className="p-3 bg-slate-900 border border-slate-800 rounded-2xl hover:bg-slate-800 transition-colors">
          <ChevronLeft size={24}/>
        </button>
        <div>
          <h1 className="text-2xl font-black italic tracking-tighter uppercase">{t('settings.title')}</h1>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">{t('settings.subtitle')}</p>
        </div>
      </div>

      {/* Profile Settings Section */}
      <section className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-6 shadow-xl">
        <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <UserIcon size={14}/> {t('settings.profile')}
        </h2>

        {/* Username Change */}
        <UsernameChangeSection user={user} t={t} />

      </section>

      {/* General Settings Section */}
      <section className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-6 shadow-xl">
        <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <Globe size={14}/> {t('settings.general')}
        </h2>

        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-2xl border border-slate-700/50">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-slate-900 rounded-xl text-emerald-500">
              <Globe size={20}/>
            </div>
            <div>
              <h4 className="text-xs font-black uppercase text-white tracking-widest">{t('settings.language')}</h4>
              <p className="text-[10px] text-slate-500 font-medium">{t('settings.language_desc')}</p>
            </div>
          </div>
          <select 
            value={lang} 
            onChange={handleLanguageChange}
            className="bg-slate-900 border border-slate-700 rounded-xl p-3 text-xs font-bold text-white outline-none focus:border-emerald-500 transition-colors cursor-pointer"
          >
            <option value="en">🇬🇧 English</option>
            <option value="it">🇮🇹 Italiano</option>
            <option value="es">🇪🇸 Español</option>
            <option value="fr">🇫🇷 Français</option>
            <option value="pt">🇵🇹 Português</option>
            <option value="de">🇩🇪 Deutsch</option>
            <option value="pl">🇵🇱 Polski</option>
            <option value="nl">🇳🇱 Nederlands</option>
            <option value="ru">🇷🇺 Русский</option>
          </select>
        </div>
      </section>

      <div className="space-y-6">
        {/* Categories */}
        <section className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
          <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4">{t('settings.notification_types')}</h2>
          
          <ToggleItem 
            icon={<Flame className="text-orange-500"/>} 
            label={t('settings.notification_motivational')} 
            sub={t('settings.notification_motivational_desc')}
            checked={settings.categories.motivational}
            onToggle={() => toggleCategory('motivational')}
          />
          <ToggleItem 
            icon={<Users className="text-blue-500"/>} 
            label={t('settings.notification_social')} 
            sub={t('settings.notification_social_desc')}
            checked={settings.categories.social}
            onToggle={() => toggleCategory('social')}
          />
          <ToggleItem 
            icon={<Trophy className="text-purple-500"/>} 
            label={t('settings.notification_challenges')} 
            sub={t('settings.notification_challenges_desc')}
            checked={settings.categories.challenge}
            onToggle={() => toggleCategory('challenge')}
          />
          <ToggleItem 
            icon={<Sparkles className="text-cyan-400"/>} 
            label={t('settings.notification_ai_insights')} 
            sub={t('settings.notification_ai_insights_desc')}
            checked={settings.categories.ai_insight}
            onToggle={() => toggleCategory('ai_insight')}
          />
        </section>

        {/* DND & Smart Limits */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-4">
            <div className="flex items-center gap-3 text-slate-400 font-black text-xs uppercase tracking-widest">
              <Moon size={16}/> {t('settings.dnd')}
            </div>
            <div className="flex items-center gap-4">
              <input 
                type="time" 
                value={settings.doNotDisturb.start}
                onChange={e => updateSettings({ doNotDisturb: { ...settings.doNotDisturb, start: e.target.value } })}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl p-3 text-xs font-bold outline-none"
              />
              <span className="text-slate-600 font-black">{t('settings.dnd_to')}</span>
              <input 
                type="time" 
                value={settings.doNotDisturb.end}
                onChange={e => updateSettings({ doNotDisturb: { ...settings.doNotDisturb, end: e.target.value } })}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl p-3 text-xs font-bold outline-none"
              />
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-4">
            <div className="flex items-center gap-3 text-slate-400 font-black text-xs uppercase tracking-widest">
              <Zap size={16}/> {t('settings.daily_limit')}
            </div>
            <div className="flex items-center gap-4">
               <input 
                 type="range" min="1" max="10" 
                 value={settings.maxPerDay}
                 onChange={e => updateSettings({ maxPerDay: parseInt(e.target.value) })}
                 className="flex-1 accent-emerald-500"
               />
               <span className="w-8 text-center font-black text-emerald-400">{settings.maxPerDay}</span>
            </div>
            <p className="text-[9px] text-slate-600 font-bold uppercase italic">{t('settings.excludes_critical')}</p>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <section className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] space-y-6 shadow-xl">
        <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <MessageSquare size={14}/> {t('settings.help_feedback')}
        </h2>
        
        <button
          onClick={() => setShowFeedback(true)}
          className="w-full p-6 bg-slate-800/30 rounded-2xl border border-slate-700/50 hover:border-emerald-500/50 hover:bg-slate-800/50 transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-500 group-hover:bg-emerald-500/20 transition-colors">
              <MessageSquare size={20} />
            </div>
            <div className="text-left">
              <h4 className="text-xs font-black uppercase text-white tracking-widest">{t('settings.send_feedback')}</h4>
              <p className="text-[10px] text-slate-500 font-medium">{t('settings.feedback_desc')}</p>
            </div>
          </div>
          <ChevronLeft size={20} className="text-slate-500 group-hover:text-emerald-400 rotate-180 transition-colors" />
        </button>
      </section>

      {showFeedback && (
        <FeedbackForm
          user={user}
          onClose={() => setShowFeedback(false)}
          initialView={currentView}
          initialPlan={user.plan}
          initialLanguage={lang}
        />
      )}
    </div>
  );
};

const ToggleItem = ({ icon, label, sub, checked, onToggle }: any) => (
  <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-2xl border border-slate-700/50 hover:bg-slate-800/50 transition-all">
    <div className="flex items-center gap-4">
      <div className="p-2 bg-slate-900 rounded-xl">{icon}</div>
      <div>
        <h4 className="text-xs font-black uppercase text-white tracking-widest">{label}</h4>
        <p className="text-[10px] text-slate-500 font-medium">{sub}</p>
      </div>
    </div>
    <button 
      onClick={onToggle}
      className={`w-12 h-6 rounded-full relative transition-all ${checked ? 'bg-emerald-500' : 'bg-slate-700'}`}
    >
      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${checked ? 'left-6.5' : 'left-0.5'}`} />
    </button>
  </div>
);